import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { orderService } from '../../services/order.service';
import { theme } from '../../config/theme';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ArrowLeftIcon, PackageIcon, TruckIcon, MapPinIcon, CreditCardIcon, CheckCircleIcon } from '../../components/icons/Icons';

const FULFILLMENT_STEPS = ['pending', 'processing', 'packed', 'shipped', 'out_for_delivery', 'delivered'];

const StatusBadge = ({ status, size = 'sm' }) => {
  const color = getStatusColor(status);
  const label = status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  return (
    <span style={{
      display: 'inline-block',
      padding: size === 'lg' ? '6px 16px' : '4px 12px',
      borderRadius: theme.borderRadius.full,
      fontSize: size === 'lg' ? theme.typography.fontSize.sm : theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.semibold,
      color: '#fff',
      backgroundColor: color,
    }}>
      {label}
    </span>
  );
};

const ProgressTracker = ({ currentStatus }) => {
  const currentIdx = FULFILLMENT_STEPS.indexOf(currentStatus);
  const isCancelled = currentStatus === 'cancelled';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', padding: `${theme.spacing.md} 0` }}>
      {FULFILLMENT_STEPS.map((step, idx) => {
        const isCompleted = !isCancelled && idx <= currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <React.Fragment key={step}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70 }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: isCompleted ? theme.colors.success : theme.colors.gray[200],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 'bold',
                border: isCurrent ? `2px solid ${theme.colors.primary.main}` : 'none',
                transition: theme.transitions.base,
              }}>
                {isCompleted ? '✓' : idx + 1}
              </div>
              <span style={{
                fontSize: '10px',
                marginTop: 4,
                color: isCompleted ? theme.colors.text.primary : theme.colors.text.disabled,
                textTransform: 'capitalize',
                textAlign: 'center',
                fontWeight: isCurrent ? theme.typography.fontWeight.semibold : 'normal',
              }}>
                {step.replace(/_/g, ' ')}
              </span>
            </div>
            {idx < FULFILLMENT_STEPS.length - 1 && (
              <div style={{
                flex: 1,
                height: 2,
                minWidth: 20,
                backgroundColor: !isCancelled && idx < currentIdx ? theme.colors.success : theme.colors.gray[200],
                marginBottom: 20,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelForm, setShowCancelForm] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await orderService.getOrderById(id);
        setOrder(response.data?.data?.order || response.data?.order || null);
      } catch (error) {
        console.error('Failed to fetch order:', error);
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleCancel = async () => {
    if (cancelReason.length < 10) {
      toast.error('Please provide a reason (at least 10 characters)');
      return;
    }
    setCancelling(true);
    try {
      await orderService.cancelOrder(id, cancelReason);
      toast.success('Order cancelled successfully');
      // Refresh
      const response = await orderService.getOrderById(id);
      setOrder(response.data?.data?.order || response.data?.order || null);
      setShowCancelForm(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <MainLayout><LoadingSpinner fullScreen /></MainLayout>;
  if (!order) return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, textAlign: 'center' }}>
        <h2>Order not found</h2>
        <Button variant="primary" onClick={() => navigate('/orders')} style={{ marginTop: theme.spacing.lg }}>
          Back to Orders
        </Button>
      </div>
    </MainLayout>
  );

  const canCancel = ['pending', 'processing'].includes(order.status);
  const shippingAddress = order.shipping_address || {};

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.xl }}>
          <button
            onClick={() => navigate('/orders')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: theme.spacing.xs }}
          >
            <ArrowLeftIcon size={24} color={theme.colors.text.secondary} />
          </button>
          <div>
            <h1 style={{ fontSize: isMobile ? theme.typography.fontSize.xl : theme.typography.fontSize['2xl'], fontWeight: 'bold', margin: 0 }}>
              Order #{order.order_number}
            </h1>
            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
              Placed on {formatDate(order.createdAt || order.created_at, 'long')}
            </span>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            {(() => {
              if (order.status === 'cancelled' || order.status === 'refunded') {
                return <StatusBadge status={order.status} size="lg" />;
              }
              let aggregateStatus = order.status;
              if (order.items && order.items.length > 0) {
                 const statuses = order.items.map(i => i.fulfillment_status);
                 const lowestIdx = Math.min(...statuses.map(s => FULFILLMENT_STEPS.indexOf(s)).filter(idx => idx !== -1));
                 aggregateStatus = lowestIdx !== Infinity ? FULFILLMENT_STEPS[lowestIdx] : order.status;
              }
              return <StatusBadge status={aggregateStatus} size="lg" />;
            })()}
          </div>
        </div>

        {/* Delivery Progress */}
        {order.status !== 'cancelled' && order.status !== 'refunded' && (
          <Card padding="lg" style={{ marginBottom: theme.spacing.xl }}>
            <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.sm }}>
              <TruckIcon size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Delivery Status
            </h3>
            {(() => {
              let aggregateStatus = order.status;
              if (order.items && order.items.length > 0) {
                 const statuses = order.items.map(i => i.fulfillment_status);
                 const lowestIdx = Math.min(...statuses.map(s => FULFILLMENT_STEPS.indexOf(s)).filter(idx => idx !== -1));
                 aggregateStatus = lowestIdx !== Infinity ? FULFILLMENT_STEPS[lowestIdx] : order.status;
              }
              return <ProgressTracker currentStatus={aggregateStatus} />;
            })()}
          </Card>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: theme.spacing.xl }}>
          {/* Left Column - Items */}
          <div>
            <Card padding="lg" style={{ marginBottom: theme.spacing.xl }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.lg }}>
                <PackageIcon size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Items ({order.items?.length || 0})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                {order.items?.map(item => (
                  <div key={item.id} style={{
                    display: 'flex',
                    gap: theme.spacing.md,
                    padding: theme.spacing.md,
                    borderRadius: theme.borderRadius.lg,
                    backgroundColor: theme.colors.gray[50],
                  }}>
                    {item.product_image ? (
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        style={{ width: 72, height: 72, borderRadius: theme.borderRadius.md, objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: 72, height: 72,
                        borderRadius: theme.borderRadius.md,
                        backgroundColor: theme.colors.gray[200],
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <PackageIcon size={28} color={theme.colors.gray[400]} />
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: 4 }}>
                        {item.product_name}
                      </div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                        Qty: {item.quantity} × {formatCurrency(item.price)}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                        <StatusBadge status={item.fulfillment_status} />
                        <span style={{ fontWeight: theme.typography.fontWeight.bold }}>
                          {formatCurrency(item.subtotal)}
                        </span>
                      </div>
                      {item.seller && (
                        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled, marginTop: 4 }}>
                          Sold by: {item.seller?.user?.first_name || item.seller?.store_name || 'Seller'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Right Column - Summary, Address, Payment */}
          <div>
            {/* Price Breakdown */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                Order Summary
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                  <span>Subtotal</span><span>{formatCurrency(order.subtotal)}</span>
                </div>
                {parseFloat(order.tax) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span>Tax</span><span>{formatCurrency(order.tax)}</span>
                  </div>
                )}
                {parseFloat(order.shipping_cost) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span>Shipping</span><span>{formatCurrency(order.shipping_cost)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  borderTop: `1px solid ${theme.colors.gray[200]}`,
                  paddingTop: theme.spacing.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  fontSize: theme.typography.fontSize.lg,
                }}>
                  <span>Total</span>
                  <span style={{ color: theme.colors.primary.main }}>{formatCurrency(order.total)}</span>
                </div>
              </div>
            </Card>

            {/* Shipping Address */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                <MapPinIcon size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Shipping Address
              </h3>
              <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, lineHeight: 1.7 }}>
                <div style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                  {shippingAddress.first_name} {shippingAddress.last_name}
                </div>
                <div>{shippingAddress.address_line1}</div>
                {shippingAddress.address_line2 && <div>{shippingAddress.address_line2}</div>}
                <div>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}</div>
                {shippingAddress.phone_number && <div>Phone: {shippingAddress.phone_number}</div>}
              </div>
            </Card>

            {/* Cancel Order */}
            {canCancel && (
              <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
                {!showCancelForm ? (
                  <Button variant="outline" fullWidth onClick={() => setShowCancelForm(true)}
                    style={{ color: theme.colors.error, borderColor: theme.colors.error }}
                  >
                    Cancel Order
                  </Button>
                ) : (
                  <div>
                    <h4 style={{ marginBottom: theme.spacing.sm, color: theme.colors.error }}>Cancel Order</h4>
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Please provide a reason for cancellation (min 10 characters)..."
                      rows={3}
                      style={{
                        width: '100%', padding: theme.spacing.sm,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.gray[300]}`,
                        fontSize: theme.typography.fontSize.sm,
                        resize: 'vertical',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
                      <Button variant="outline" size="sm" onClick={() => setShowCancelForm(false)}>
                        Never mind
                      </Button>
                      <Button
                        size="sm"
                        loading={cancelling}
                        onClick={handleCancel}
                        style={{ backgroundColor: theme.colors.error }}
                      >
                        Confirm Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default OrderDetails;
